/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  Column,
  ColumnDef,
  ColumnDefResolved,
  CoreInstance,
  CoreTableState,
  GroupColumnDef,
  Row,
  RowData,
  Table,
  TableOptionsResolved,
  TableState,
  Updater,
} from './types.js';
import {
  isDev,
  isProduction,
} from '../../../../core/utils/is-node-environment.js';
import { DataTableColumnFontStyle } from '../../features/ColumnFontStyle/ColumnFontStyle.js';
import { DataTableColumnOrder } from '../../features/ColumnOrder/ColumnOrder.js';
import { DataTableColumnPinning } from '../../features/ColumnPinning/ColumnPinning.js';
import { DataTableColumnSizing } from '../../features/ColumnSizing/ColumnSizing.js';
import { DataTableColumnVisibility } from '../../features/ColumnVisibility/ColumnVisibility.js';
import { DataTableDownloadData } from '../../features/Download/Download.js';
import { DataTableGanttChartFeature } from '../../features/GanttChart/GanttChart.js';
import { Layout } from '../../features/Layout/Layout.js';
import { LineWrap } from '../../features/LineWrap/LineWrap.js';
import { DataTablePaginationFeature } from '../../features/Pagination/Pagination.js';
import { DataTableRowDetails } from '../../features/RowDetails/RowDetails.js';
import { DataTableRowInteractivity } from '../../features/RowInteractivity/RowInteractivity.js';
import { DataTableRowOrder } from '../../features/RowOrder/RowOrder.js';
import { DataTableRowSelection } from '../../features/RowSelection/RowSelection.js';
import { Sorting as DataTableSorting } from '../../features/Sorting/Sorting.js';
import { DataTableSubRows } from '../../features/SubRows/SubRows.js';
import { Thresholds } from '../../features/Thresholds/Thresholds.js';
import { UserActions } from '../../features/UserActions/UserActions.js';
import { DataTableVirtualScrolling } from '../../features/VirtualScrolling/VirtualScrolling.js';
import { VisualVariantConfig } from '../../features/VisualVariants/VisualVariants.js';
import {
  ColumnFaceting,
  ColumnFiltering,
  ColumnGrouping,
  ColumnOrdering,
  ColumnPinning,
  ColumnSizing,
  ColumnVisibility,
  createColumn,
  functionalUpdate,
  GlobalFaceting,
  GlobalFiltering,
  Headers,
  memo,
  RequiredKeys,
  RowExpanding,
  RowPagination,
  RowSelection,
  RowSorting,
} from '../../tanstack-table-core-override/src/index.js';

const features = [
  Headers,
  ColumnVisibility,
  ColumnOrdering,
  ColumnPinning,
  ColumnFaceting,
  ColumnFiltering,
  GlobalFaceting, //depends on ColumnFaceting
  GlobalFiltering, //depends on ColumnFiltering
  RowSorting,
  ColumnGrouping,
  RowExpanding,
  RowPagination,
  RowSelection,
  ColumnSizing,
  DataTableColumnSizing,
  LineWrap,
  Layout,
  VisualVariantConfig,
  UserActions,
  DataTableSorting,
  DataTableColumnVisibility,
  DataTableColumnOrder,
  DataTableRowSelection,
  Thresholds,
  DataTableSubRows,
  DataTableRowDetails,
  DataTableRowInteractivity,
  DataTableVirtualScrolling,
  DataTableDownloadData,
  DataTableColumnFontStyle,
  DataTablePaginationFeature,
  DataTableRowOrder,
  DataTableColumnPinning,
  DataTableGanttChartFeature,
] as const;

/** @internal */
export function createTable<TData extends RowData>(
  options: TableOptionsResolved<TData>,
): Table<TData> {
  if (options.debugAll || options.debugTable) {
    // eslint-disable-next-line no-console
    console.info('Creating Table Instance...');
  }

  const table = { _features: features } as unknown as Table<TData>;

  const defaultOptions = table._features.reduce((obj, feature) => {
    return Object.assign(obj, feature.getDefaultOptions?.(table));
  }, {}) as TableOptionsResolved<TData>;

  const mergeOptions = (options: TableOptionsResolved<TData>) => {
    if (table.options.mergeOptions) {
      return table.options.mergeOptions(defaultOptions, options);
    }

    return {
      ...defaultOptions,
      ...options,
    };
  };

  const coreInitialState: CoreTableState = {};

  let initialState = {
    ...coreInitialState,
    ...(options.initialState ?? {}),
  } as TableState;

  table._features.forEach((feature) => {
    initialState = feature.getInitialState?.(initialState) ?? initialState;
  });

  const queued: (() => void)[] = [];
  let queuedTimeout = false;

  const coreInstance: CoreInstance<TData> = {
    _features: features,
    options: {
      ...defaultOptions,
      ...options,
    },
    initialState,
    _queue: (cb) => {
      queued.push(cb);

      if (!queuedTimeout) {
        queuedTimeout = true;

        // Schedule a microtask to run the queued callbacks after
        // the current call stack (render, etc) has finished.
        Promise.resolve()
          .then(() => {
            while (queued.length) {
              queued.shift()?.();
            }
            queuedTimeout = false;
          })
          .catch((error) =>
            setTimeout(() => {
              throw error;
            }),
          );
      }
    },
    reset: () => {
      table.setState(table.initialState);
    },
    setOptions: (updater) => {
      const newOptions = functionalUpdate(updater, table.options);
      table.options = mergeOptions(newOptions) as RequiredKeys<
        TableOptionsResolved<TData>,
        'state'
      >;
    },

    getState: () => {
      return table.options.state as TableState;
    },

    setState: (updater: Updater<TableState>) => {
      table.options.onStateChange?.(updater);
    },

    _getRowId: (row: TData, index: number, parent?: Row<TData>) =>
      table.options.getRowId?.(row, index, parent) ??
      `${parent ? [parent.id, index].join('.') : index}`,

    getCoreRowModel: () => {
      table._getCoreRowModel ??= table.options.getCoreRowModel(table);

      return table._getCoreRowModel();
    },

    // The final calls start at the bottom of the model,
    // row ordering, which then work their way up
    getRowModel: () => {
      return table.getRowDetailsRowModel();
    },
    getRow: (id: string, searchAll?: boolean) => {
      const row = (searchAll ? table.getCoreRowModel() : table.getRowModel())
        .rowsById[id];

      if (!row) {
        if (!isProduction()) {
          throw new Error(`getRow expected an ID, but got ${id}`);
        }
        throw new Error();
      }

      return row;
    },
    _getDefaultColumnDef: memo(
      () => [table.options.defaultColumn],
      (defaultColumn) => {
        defaultColumn = (defaultColumn ?? {}) as Partial<
          ColumnDef<TData, unknown>
        >;

        return {
          header: (props) => {
            const resolvedColumnDef = props.header.column
              .columnDef as ColumnDefResolved<TData>;

            if (resolvedColumnDef.accessorKey) {
              return resolvedColumnDef.accessorKey;
            }

            if (resolvedColumnDef.accessorFn) {
              return resolvedColumnDef.id;
            }

            return null;
          },
          // footer: props => props.header.column.id,
          cell: (props) => props.renderValue<any>()?.toString?.() ?? null,
          ...table._features.reduce((obj, feature) => {
            return Object.assign(obj, feature.getDefaultColumnDef?.());
          }, {}),
          ...defaultColumn,
        } as Partial<ColumnDef<TData, unknown>>;
      },
      {
        debug: () => table.options.debugAll ?? table.options.debugColumns,
        key: isDev() && 'getDefaultColumnDef',
      },
    ),

    _getColumnDefs: () => table.options.columns,

    getAllColumns: memo(
      () => [table._getColumnDefs()],
      (columnDefs) => {
        const recurseColumns = (
          columnDefs: ColumnDef<TData, unknown>[],
          parent?: Column<TData, unknown>,
          depth = 0,
        ): Column<TData, unknown>[] => {
          return columnDefs.map((columnDef) => {
            const column = createColumn(
              // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
              // @ts-ignore Strict mode transition - needs to be investigated
              table,
              columnDef,
              depth,
              parent,
            ) as unknown as Column<TData, unknown>;

            const groupingColumnDef = columnDef as GroupColumnDef<
              TData,
              unknown
            >;

            column.columns = groupingColumnDef.columns
              ? recurseColumns(groupingColumnDef.columns, column, depth + 1)
              : [];

            return column;
          });
        };

        return recurseColumns(columnDefs);
      },
      {
        key: isDev() && 'getAllColumns',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllFlatColumns: memo(
      () => [table.getAllColumns()],
      (allColumns) => {
        return allColumns.flatMap((column) => {
          return column.getFlatColumns() as Column<TData, unknown>[];
        });
      },
      {
        key: isDev() && 'getAllFlatColumns',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllFlatColumnIdsWithoutBuiltin: memo(
      () => [table.getAllColumns()],
      () => {
        return table
          .getAllFlatColumns()
          .filter((leafColumn) => !leafColumn.columnDef.meta?.builtin)
          .map((column) => column.id);
      },
      {
        key: isDev() && 'getAllFlatColumnIdsWithoutBuiltin',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    _getAllFlatColumnsById: memo(
      () => [table.getAllFlatColumns()],
      (flatColumns) => {
        return (flatColumns as Column<TData, unknown>[]).reduce(
          (acc, column) => {
            acc[column.id] = column;
            return acc;
          },
          {} as Record<string, Column<TData, unknown>>,
        );
      },
      {
        key: isDev() && 'getAllFlatColumnsById',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllLeafColumns: memo(
      () => [table.getAllColumns(), table._getOrderColumnsFn()],
      (allColumns, orderColumns) => {
        const leafColumns = allColumns.flatMap((column) =>
          column.getLeafColumns(),
        );
        // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
        // @ts-ignore Strict mode transition - needs to be investigated
        return orderColumns(leafColumns) as unknown as Column<TData, unknown>[];
      },
      {
        key: isDev() && 'getAllLeafColumns',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getAllLeafColumnsWithoutBuiltin: memo(
      () => [table.getAllLeafColumns()],
      (allLeafColumns) => {
        return allLeafColumns.filter(
          (column) => !column.columnDef.meta?.builtin,
        );
      },
      {
        key: isDev() && 'getAllLeafColumnsWithoutBuiltin',
        debug: () => table.options.debugAll ?? table.options.debugColumns,
      },
    ),

    getColumn: (columnId) => {
      const column = table._getAllFlatColumnsById()[columnId] as Column<
        TData,
        unknown
      >;

      if (!isProduction() && !column) {
        // eslint-disable-next-line no-console
        console.warn(`[Table] Column with id '${columnId}' does not exist.`);
      }

      return column;
    },

    getHeader: memo(
      (headerId) => [headerId, table.getLeafHeaders()],
      (headerId, leafHeaders) => {
        const header = leafHeaders.find((header) => header.id === headerId);

        if (!isProduction() && !header) {
          // eslint-disable-next-line no-console
          console.warn(`[Table] Header with id '${headerId}' does not exist.`);
        }
        return header;
      },
      {
        key: isDev() && 'getHeader',
        debug: () => table.options.debugAll ?? table.options.debugHeaders,
      },
    ),
  };

  Object.assign(table, coreInstance);

  for (const feature of table._features) {
    feature?.createTable?.(table);
  }

  return table;
}
